import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './DemarchesAdministratives.css';
import { 
  FaClipboardList,
  FaIdCard,
  FaUniversity,
  FaHeartbeat,
  FaHome,
  FaBriefcase,
  FaFileAlt,
  FaArrowLeft,
  FaCheckCircle,
  FaCalendarAlt,
  FaRegClock,
  FaEuroSign,
  FaPrint,
  FaRegEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaPassport,
  FaVisa,
  FaRegCreditCard,
  FaShieldAlt
} from 'react-icons/fa';

const DemarchesAdministratives = () => {
  const navigate = useNavigate();

  const administrativeProcedures = [
    {
      icon: <FaIdCard />,
      title: "Titre de séjour",
      description: "Renouvellement, changement de statut, première demande",
      steps: ["Prise de rendez-vous en ligne", "Préparation des documents", "Dépôt du dossier", "Suivi en préfecture"],
      color: "#1e3c72"
    },
    {
      icon: <FaUniversity />,
      title: "Inscription universitaire",
      description: "Inscription administrative et pédagogique",
      steps: ["Validation des acquis", "Paiement des droits", "Carte étudiante", "Certificat de scolarité"],
      color: "#2a5298"
    },
    {
      icon: <FaHeartbeat />,
      title: "Sécurité sociale",
      description: "Affiliation à l'assurance maladie",
      steps: ["Inscription CPAM", "Carte Vitale", "Mutuelle complémentaire", "Remboursements"],
      color: "#2e8b57"
    },
    {
      icon: <FaHome />,
      title: "CAF - Aide au logement",
      description: "Demande d'APL/ALS",
      steps: ["Création compte CAF", "Dépôt du dossier", "Justificatifs", "Versement mensuel"],
      color: "#32cd32"
    },
    {
      icon: <FaBriefcase />,
      title: "Autorisation de travail",
      description: "Droit de travailler pendant les études",
      steps: ["Vérification du droit", "Déclaration employeur", "Limite d'heures", "Suivi"],
      color: "#ff3855"
    },
    {
      icon: <FaRegCreditCard />,
      title: "Ouverture compte bancaire",
      description: "Compte bancaire en France",
      steps: ["Choix de la banque", "Rendez-vous", "RIB", "Moyens de paiement"],
      color: "#ff8c00"
    }
  ];

  const requiredDocuments = [
    {
      category: "Identité",
      icon: <FaPassport />,
      documents: [
        "Passeport en cours de validité",
        "Visa long séjour valide",
        "Photos d'identité (3-4 exemplaires)",
        "Justificatif d'état civil (traduit si nécessaire)"
      ]
    },
    {
      category: "Scolarité",
      icon: <FaUniversity />,
      documents: [
        "Certificat de scolarité de l'année en cours",
        "Relevés de notes des années précédentes",
        "Diplômes obtenus (traduits)",
        "Attestation de réussite ou d'assiduité"
      ]
    },
    {
      category: "Hébergement",
      icon: <FaHome />,
      documents: [
        "Justificatif de domicile (quittance, facture)",
        "Contrat de location",
        "Attestation d'hébergement si logé chez un tiers",
        "Quittance de loyer des 3 derniers mois"
      ]
    },
    {
      category: "Financier",
      icon: <FaEuroSign />,
      documents: [
        "Justificatifs de ressources (615€/mois minimum)",
        "Relevés bancaires des 3-6 derniers mois",
        "Attestation de bourse",
        "Garant ou caution si nécessaire"
      ]
    },
    {
      category: "Santé",
      icon: <FaHeartbeat />,
      documents: [
        "Attestation d'inscription à la sécurité sociale",
        "Carte Vitale",
        "Mutuelle étudiante",
        "Certificat médical si nécessaire"
      ]
    }
  ];

  const timeline = [
    {
      month: "Septembre - Octobre",
      tasks: [
        "Inscription universitaire",
        "Affiliation sécurité sociale",
        "Ouverture compte bancaire",
        "Demande CAF"
      ]
    },
    {
      month: "Novembre - Décembre",
      tasks: [
        "Réception carte Vitale",
        "Début versements CAF",
        "Validation inscription définitive",
        "Carte étudiante"
      ]
    },
    {
      month: "Janvier - Février",
      tasks: [
        "Déclaration d'impôts (si revenus)",
        "Renouvellement titre séjour (2-3 mois avant expiration)",
        "Vérification droits CAF"
      ]
    },
    {
      month: "Mars - Mai",
      tasks: [
        "Préparation renouvellement titre séjour",
        "Inscription année suivante",
        "Dossier de bourses"
      ]
    },
    {
      month: "Juin - Août",
      tasks: [
        "Validation année universitaire",
        "Renouvellement titre séjour",
        "Recherche logement pour l'année suivante"
      ]
    }
  ];

  const usefulLinks = [
    {
      name: "ANEF - Titres de séjour",
      url: "https://administration-etrangers-en-france.interieur.gouv.fr",
      description: "Dépôt et suivi des demandes de titre de séjour"
    },
    {
      name: "Ameli - Sécurité sociale",
      url: "https://www.ameli.fr",
      description: "Gestion de votre compte et remboursements"
    },
    {
      name: "CAF - Aide au logement",
      url: "https://www.caf.fr",
      description: "Demande et suivi des aides au logement"
    },
    {
      name: "Campus France",
      url: "https://www.campusfrance.org",
      description: "Informations pour étudiants internationaux"
    },
    {
      name: "Service Public",
      url: "https://www.service-public.fr",
      description: "Toutes les démarches administratives"
    }
  ];

  const faqs = [
    {
      question: "Quel est le délai pour obtenir un titre de séjour ?",
      answer: "Le délai d'instruction est généralement de 2 à 4 mois. Il est conseillé de déposer votre dossier 2-3 mois avant l'expiration de votre titre actuel."
    },
    {
      question: "Comment obtenir ma carte Vitale ?",
      answer: "Après votre inscription à la sécurité sociale, vous recevrez un numéro provisoire puis la carte Vitale définitive dans un délai de 2-3 mois."
    },
    {
      question: "Quels sont mes droits au travail ?",
      answer: "Les étudiants étrangers peuvent travailler jusqu'à 964 heures par an (60% de la durée légale). Une autorisation de travail n'est pas nécessaire si vous avez un titre de séjour étudiant."
    },
    {
      question: "Comment déclarer mon logement à la CAF ?",
      answer: "Créez un compte sur caf.fr, remplissez le formulaire en ligne et fournissez les justificatifs demandés (contrat de location, quittances, RIB, etc.)"
    },
    {
      question: "Puis-je bénéficier d'une bourse ?",
      answer: "Les étudiants étrangers peuvent bénéficier de bourses sous conditions (CROUS, bourses gouvernementales, etc.). Renseignez-vous auprès du CROUS de votre région."
    }
  ];

  return (
    <div className="da-page">
      {/* Hero Section */}
      <div className="da-hero da-hero--admin">
        <div className="da-hero__background"></div>
        <div className="da-hero__content">
          <button className="da-back-button" onClick={() => navigate('/nos-services')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="da-hero__icon">
            <FaClipboardList />
          </div>
          <h1 className="da-hero__title">Démarches Administratives</h1>
          <p className="da-hero__subtitle">Guidance pour toutes les formalités administratives nécessaires aux étudiants étrangers</p>
        </div>
      </div>

      <div className="da-content">
        <div className="da-container">
          {/* Introduction */}
          <div className="da-intro">
            <h2 className="da-section-title">Votre guide administratif en France</h2>
            <p className="da-intro__text">
              Arriver en France en tant qu'étudiant étranger implique de nombreuses démarches administratives 
              essentielles. Notre service vous accompagne pas à pas dans toutes ces procédures : titre de séjour, 
              sécurité sociale, CAF, ouverture de compte bancaire, et bien plus encore. Nous vous aidons à 
              naviguer dans le système administratif français et à ne rien oublier.
            </p>
          </div>

          {/* Procédures principales */}
          <div className="da-section">
            <h2 className="da-section-title">Principales démarches</h2>
            <div className="da-grid da-grid--procedures">
              {administrativeProcedures.map((proc, index) => (
                <div className="da-card da-card--procedure" key={index} style={{ borderTopColor: proc.color }}>
                  <div className="da-card__icon" style={{ color: proc.color }}>
                    {proc.icon}
                  </div>
                  <h3 className="da-card__title">{proc.title}</h3>
                  <p className="da-card__description">{proc.description}</p>
                  <div className="da-card__steps">
                    {proc.steps.map((step, i) => (
                      <div className="da-step" key={i}>
                        <FaCheckCircle className="da-step__check" style={{ color: proc.color }} />
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Documents requis */}
          <div className="da-section">
            <h2 className="da-section-title">Documents indispensables</h2>
            <div className="da-docs-grid">
              {requiredDocuments.map((category, index) => (
                <div className="da-doc-category" key={index}>
                  <div className="da-doc-category__header">
                    <div className="da-doc-category__icon">{category.icon}</div>
                    <h3 className="da-doc-category__title">{category.category}</h3>
                  </div>
                  <ul className="da-doc-list">
                    {category.documents.map((doc, i) => (
                      <li key={i}>
                        <FaFileAlt className="da-doc-list__icon" />
                        <span>{doc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="da-doc-note">
              <FaPrint className="da-doc-note__icon" />
              <p>Conseil : Préparez toujours plusieurs copies de chaque document. Les originaux doivent être présentés lors des rendez-vous officiels.</p>
            </div>
          </div>

          {/* Timeline */}
          <div className="da-section">
            <h2 className="da-section-title">Calendrier administratif</h2>
            <div className="da-timeline">
              {timeline.map((period, index) => (
                <div className="da-timeline__block" key={index}>
                  <div className="da-timeline__month">
                    <FaCalendarAlt />
                    <span>{period.month}</span>
                  </div>
                  <div className="da-timeline__tasks">
                    {period.tasks.map((task, i) => (
                      <div className="da-task" key={i}>
                        <FaRegClock className="da-task__icon" />
                        <span>{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Liens utiles */}
          <div className="da-section">
            <h2 className="da-section-title">Liens utiles</h2>
            <div className="da-grid da-grid--links">
              {usefulLinks.map((link, index) => (
                <a 
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="da-link-card"
                  key={index}
                >
                  <div className="da-link-card__icon">
                    <FaRegEnvelope />
                  </div>
                  <div className="da-link-card__info">
                    <h3 className="da-link-card__name">{link.name}</h3>
                    <p className="da-link-card__desc">{link.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="da-section">
            <h2 className="da-section-title">Questions fréquentes</h2>
            <div className="da-faq-list">
              {faqs.map((faq, index) => (
                <details className="da-faq-item" key={index}>
                  <summary>
                    <FaShieldAlt className="da-faq-item__icon" />
                    <span>{faq.question}</span>
                  </summary>
                  <div className="da-faq-item__answer">
                    <p>{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="da-contact">
            <h2 className="da-section-title da-section-title--centered">Besoin d'aide dans vos démarches ?</h2>
            <p className="da-contact__text">Notre équipe vous accompagne pour toutes vos formalités administratives</p>
            <div className="da-contact-buttons">
              <Link to="/contact" className="da-button da-button--primary">
                <FaPhoneAlt /> Consultation gratuite
              </Link>
              <Link to="/rendez-vous" className="da-button da-button--secondary">
                <FaMapMarkerAlt /> Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemarchesAdministratives;